<template>
    <div class="offer-list-component">
        <div v-for="offer in offers" :key="offer.id" class="offer-list-component__item">
            <div class="offer-list-component__item--container">
                <div class="offer-list-component__image">
                    <img :src="offerCoverImage(offer)" :alt="$t('alt.offer_cover')" />
                </div>

                <div class="offer-list-component__item--wrapper">
                    <div class="offer-list-component__item--title">{{ offer.titleTextContent.originalText }}</div>
                    <div class="offer-list-component__item--category">
                        {{ $t('common.microcopy.category') }}: {{ getServiceName(offer.service) }}
                    </div>
                    <div>
                        <common-icon name="schedule-cancel" size="large"></common-icon>
                        {{ dayjs(offer.expireAt) }}
                    </div>
                    <div class="offer-list-component__item--address">
                        <span @click="openMap(offer.location.latitude, offer.location.longitude)">
                            <common-icon name="city" size="large"></common-icon>
                            {{ offer.location?.address }}, {{ offer.location?.city?.name }}
                        </span>
                    </div>
                </div>
            </div>

            <div class="offer-list-component__item--pricing">
                <div
                    v-if="showActionBar(offer)"
                    type="button"
                    class="offer-list-component__item--pricing--dropdown"
                    @click.stop="toggleMenu(offer.id)"
                >
                    <common-icon name="three-horizontal-dots" size="large"></common-icon>
                    <common-dropdown
                        v-click-away="closeMenu"
                        :isOpen="isDropdownOpen(offer.id)"
                        :hasArrow="true"
                        arrowPositionY="top"
                        arrowPositionX="right"
                    >
                        <nav class="dropdown__menu">
                            <button type="button" @click="allowRedirection ? goToOffer(offer) : null">
                                {{ $t('common.microcopy.edit') }}
                            </button>
                            <button v-if="offer.extendable && !isServiceForCompany" type="button" @click="openExtendOfferModal()">
                                {{ $t('common.microcopy.extend') }}
                            </button>
                            <button type="button" @click="openDeleteOfferModal()">
                                {{ $t('common.microcopy.delete') }}
                            </button>
                        </nav>
                    </common-dropdown>
                </div>
            </div>
        </div>
        <div v-if="!offers.length" class="offer-list-component__no-offers">
            <span>{{ noOfferMessage }}</span>
        </div>
        <yes-no-modal
            v-if="isDeleteModalOpen"
            :heading="$t('modals.delete_offer.title')"
            :isLoading="isLoading"
            @close="closeDeleteOfferModal()"
            @accepted="deleteOffer(selectedOfferId)"
        ></yes-no-modal>
        <yes-no-modal
            v-if="isExtendModalOpen"
            :heading="$t('modals.extend_offer.title')"
            :isLoading="isLoading"
            @close="closeExtendOfferModal()"
            @accepted="extendOffer(selectedOfferId)"
        ></yes-no-modal>
    </div>
</template>

<script>
    import dayjs from 'dayjs'

    import YesNoModal from 'components/modals/YesNoModal'
    import OfferAPI from 'api/OfferAPI'
    import OfferTypeEnum from 'enums/offer_type.enum'
    import RoutesEnum from 'enums/routes.enum'
    import LanguageEnum from 'enums/language.enum'
    import CompanyRoles from 'enums/company_roles.enum'

    export default {
        components: {
            YesNoModal,
        },
        props: {
            offers: { type: Array, required: true },
            companyId: { type: Number, default: undefined },
            allowRedirection: { type: Boolean, default: true },
            activeTab: { type: String, default: undefined },
        },

        data() {
            return {
                placeholderImage: require('../../../assets/images/no-image-placeholder.png'),
                isDeleteModalOpen: false,
                isLoading: false,
                selectedDropdownId: null,
                selectedOfferId: null,
                isExtendModalOpen: false,
            }
        },

        computed: {
            isServiceForCompany() {
                return !!this.companyId
            },
            noOfferMessage() {
                if (this.activeTab === 'expired') {
                    return this.$t('forms.user.profile.messages.no_offers_expired')
                } else if (this.activeTab === 'renewal') {
                    return this.$t('forms.user.profile.messages.no_offers_reneewal')
                }
                return this.$t('forms.user.profile.messages.no_offers_active')
            },
        },

        methods: {
            offerCoverImage(offer) {
                return offer.photoThumbnail || this.placeholderImage
            },
            getServiceName(service) {
                return this.$i18n.locale === LanguageEnum.EN ? service.name : service[`name${this.$i18n.locale.toUpperCase()}`]
            },
            dayjs(date) {
                return dayjs(date).format('DD.MM.YYYY HH:MM')
            },
            openMap(long, lat) {
                window.open(`https://www.google.com/maps/search/?api=1&query=${long},${lat}`, '_blank')
            },
            toggleMenu(id) {
                this.selectedOfferId = id
                if (this.selectedDropdownId === id) {
                    return (this.selectedDropdownId = null)
                }
                this.selectedDropdownId = id
            },
            isDropdownOpen(id) {
                return this.selectedDropdownId === id
            },
            closeMenu() {
                this.selectedDropdownId = null
            },

            extendOffer(id) {
                this.isLoading = true

                const req = this.isServiceForCompany ? OfferAPI.extendCompanyOffer : OfferAPI.extendUserOffer

                req({ id, companyId: this.companyId }).then((response) => {
                    this.closeExtendOfferModal()
                    this.emitEvent({ name: 'offer-extended', payload: { extendedOffer: response } })
                    this.isLoading = false
                })
            },
            openDeleteOfferModal() {
                this.isDeleteModalOpen = true
            },
            closeDeleteOfferModal() {
                this.isDeleteModalOpen = false
            },
            deleteOffer(id) {
                this.isLoading = true

                const req = this.isServiceForCompany ? OfferAPI.deleteCompanyOffer : OfferAPI.deleteUserOffer

                req({ id, companyId: this.companyId }).then(() => {
                    this.closeDeleteOfferModal()
                    this.emitEvent({ name: 'offer-deleted', payload: { offerId: id } })
                    this.isLoading = false
                })
            },
            showActionBar(offer) {
                return (
                    this.allowRedirection &&
                    ((offer.location.userServiceProvider && offer.location.userServiceProvider?.id === this.$auth.getUser().id) ||
                        (offer.location.companyServiceProvider &&
                            this.$auth
                                .getUser()
                                .companyUsers.filter((item) => item.companyRole === CompanyRoles.COMPANY_ADMIN.VALUE)
                                .map((item) => item.company.id)
                                .includes(offer.location.companyServiceProvider?.id)))
                )
            },
            goToOffer(offer) {
                if (offer.location.userServiceProvider && offer.location.userServiceProvider?.id !== this.$auth.getUser().id) {
                    return
                }
                const offerType = offer.location.companyServiceProvider ? OfferTypeEnum.COMPANY : OfferTypeEnum.USER
                this.$router.push({ name: RoutesEnum.OFFER.CONFIGURE, params: { id: offer.id, type: offerType } })
            },
            openExtendOfferModal() {
                this.isExtendModalOpen = true
            },

            closeExtendOfferModal() {
                this.isExtendModalOpen = false
            },
        },
    }
</script>

<style lang="scss" scoped>
    @import 'styles/components/offers/_offer-list-component.scss';
</style>
